import { type FC, useEffect, useMemo, useState } from "react";

import moment from "moment";

import {
  type CursorPaginationState,
  type DateRange,
  type DateRangePartial,
  DateRangePicker,
  type FilterState,
  Link,
  Page,
  Table,
  type TableColumn,
  type TableFilter,
} from "@alpacahq/ui/old";

import { useAccountContext } from "~/v2/providers/AccountProvider";
import { formatAmount } from "~/v2/utils/formatAmount";
import { useGetPaginatedActivities } from "../../../../api/hooks";
import type { Activity } from "../../../../api/rest/account";
import { Path } from "../../../../path";
import {
  getPrettyCash,
  getPrettyDate,
  getPrettyDateFormatted,
} from "../../../../utils/formatting";

const ACTIVITY_FILTER_TYPES = {
  TRANSFERS: ["TRANS", "CSD", "CSW", "CSR"],
  FEES: ["FEE", "DIVFEE", "CFEE"],
  TRADES: ["FILL"],
};

type Filters = {
  activityTypes?: string[];
  after?: string;
  until?: string;
};

const formatDates = (
  dates: [Date | null, Date | null],
): [Date | null, Date | null] => {
  const dateFrom = dates[0] !== null ? new Date(dates[0].toDateString()) : null;
  dates[0]?.toLocaleString("en-US", { timeZone: "America/New_York" });

  const dateTo = dates[1];
  dateTo?.toLocaleString("en-US", { timeZone: "America/New_York" });
  dateTo?.setHours(23);
  dateTo?.setMinutes(59);
  dateTo?.setSeconds(59);

  return [dateFrom, dateTo];
};

const Activities: FC = () => {
  const [dateRange, setDateRange] = useState<DateRangePartial>([null, null]);
  const [filters, setFilters] = useState<Filters>({});
  const [pageSize, setPageSize] = useState(20);
  const [prevToken, setPrevToken] = useState<Array<string>>([]);
  const [nextToken, setNextToken] = useState("");
  const [activeToken, setActiveToken] = useState<string | undefined>(undefined);

  const resetPagination = () => {
    setPrevToken([]);
    setNextToken("");
    setActiveToken(undefined);
  };

  const onDateChange = (dates: [Date | null, Date | null]) => {
    const d = formatDates(dates);
    setDateRange(d as DateRange);
    setFilters((prevState) => ({
      ...prevState,
      after: d[0]?.toISOString(),
      until: d[1]?.toISOString(),
    }));
    resetPagination();
  };

  const onClearDate = () => {
    setFilters((prevState) => ({
      ...prevState,
      after: undefined,
      until: undefined,
    }));
    resetPagination();
  };

  const onPaginationChange = (cursorState: CursorPaginationState) => {
    if (cursorState.pageSize !== pageSize) {
      setPageSize(cursorState.pageSize);
    }

    if (cursorState.currentCursor.next === "") {
      paginateToPrev();
    } else {
      paginateToNext();
    }
  };

  const paginateToPrev = () => {
    const prev = [...prevToken].pop();
    setPrevToken((prevState) => prevState.slice(0, -1));
    setActiveToken(prev === "FIRST" ? undefined : prev);
  };

  const paginateToNext = () => {
    setPrevToken((prevTokens) => [...prevTokens, activeToken ?? "FIRST"]);
    setActiveToken(nextToken);
  };

  const { currentAccount, product } = useAccountContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies: currentAccount?.id is the trigger: pagination resets when the user switches account. resetPagination is redefined every render, so listing it would reset pagination on every render and the table could never be paged.
  useEffect(() => {
    resetPagination();
  }, [currentAccount?.id]);

  const { data } = useGetPaginatedActivities({
    accountID: currentAccount?.id,
    paper: product === "paper",
    pageSize,
    pageToken: activeToken,
    filters,
    options: {
      enabled: !!currentAccount?.id,
    },
  });
  const activities: Activity[] = data ?? [];

  const getDate = (date?: string, transaction_time?: string) => {
    if (date) {
      return getPrettyDateFormatted(
        moment(date).toDate().getTime(),
        "MMM DD, YYYY",
      );
    }

    if (transaction_time) {
      return getPrettyDate(transaction_time);
    }

    return "-";
  };

  const onActivityTypesChange = (updatedValues: FilterState) => {
    const type = updatedValues?.activity_type?.[0]?.trim();

    setFilters((prevFilters) => ({
      ...prevFilters,
      activityTypes: type
        ? Object.hasOwn(ACTIVITY_FILTER_TYPES, type)
          ? ACTIVITY_FILTER_TYPES[type as keyof typeof ACTIVITY_FILTER_TYPES]
          : [type]
        : undefined,
    }));
    resetPagination();
  };

  const tableFilters: TableFilter<Activity>[] = useMemo(
    () => [
      {
        key: "activity_type",
        label: "Activity Type",
        options: [
          { label: "Trades", value: "FILL" },
          { label: "Transfers", value: "TRANSFERS" },
          { label: "Dividends", value: "DIV" },
          { label: "Fees", value: "FEES" },
          { label: "Interests", value: "INT" },
        ],
        filterFn: () => true,
      },
    ],
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: getDate is a local helper redefined every render; listing it would rebuild the whole column set on every render. The columns are intentionally built once because they close over nothing that changes.
  const columns = useMemo<TableColumn<Activity>[]>(
    () => [
      {
        name: "Description",
        key: "description",
        cellRenderer: ({
          activity_type,
          activity_sub_type,
          qty = "",
          side = "",
          symbol = "",
          description = "",
        }) => {
          switch (activity_type) {
            case "CSD":
              return "Cash Deposit";
            case "CSW":
              return "Cash Withdrawal";
            case "DIV":
              return `Dividend from ${symbol}`;
            case "FILL":
              return `${
                side.charAt(0).toUpperCase() + side.slice(1)
              } ${qty} ${symbol}`;
            case "DIVFEE":
              return "Dividend fee";
            case "CFEE": {
              const feeStr = "Crypto fee ";
              if (activity_sub_type === "FND") {
                return `${feeStr}- Perps Funding`;
              }
              return feeStr;
            }
            case "OPEXC":
              return `Option Exercise: ${symbol}`;
            case "OPTRD":
              return `Option Trade: ${qty} - ${symbol}`;
            case "OPEXP":
              return `Option Expiry: ${symbol}`;
            case "OPASN":
              return `Option Assignment: ${symbol}`;
            case "JNLC":
              return "Journal cash between accounts";
            default:
              return description || "–";
          }
        },
      },
      {
        name: "Type",
        key: "activity_type",
      },
      {
        name: "Qty",
        key: "qty",
        alignRight: true,
        cellRenderer: ({ qty }) =>
          formatAmount(qty, {
            maximumFractionDigits: 8,
          }),
      },
      {
        name: "Amount",
        key: "amount",
        alignRight: true,
        cellRenderer: ({ price, qty, net_amount }) =>
          getPrettyCash(net_amount || parseFloat(price) * parseFloat(qty), {
            withPrefix: true,
            forcePrecision: 2,
            minimumFractionDigits: 2,
          }),
      },
      {
        name: "Date",
        key: "transaction_time",
        alignRight: true,
        cellRenderer: ({ date, transaction_time }) =>
          getDate(date, transaction_time),
      },
    ],
    [],
  );

  // replace next page token every time new pages come in
  useEffect(() => {
    if (activities.length && activities.length === pageSize) {
      setNextToken(activities[activities.length - 1].id);
    } else {
      // there is no next page token when pagesize is not met or no activities are returned
      setNextToken("");
    }
  }, [activities, pageSize]);

  return (
    <Page
      title="Activities"
      subtitle={
        <>
          <span>View your activity history</span>
          <span className="block pt-2 text-md">
            Learn more about activity types in our{" "}
            <Link href={Path.URL_DOCS_ACCOUNT_ACTIVITIES}>documentation</Link>
          </span>
        </>
      }
      className="p-8 pt-0 xs:pl-0 md:p-8 md:pt-0 md:pl-0"
    >
      <Table
        uniqueKey="activities-table"
        columns={columns}
        rows={activities}
        emptyMessage="No activities to show. Place a trade via the dashboard or the API to see this table populate"
        searchEnabled
        searchPlaceholder="Search by type..."
        searchColumns={["activity_type"]}
        onFilterChange={onActivityTypesChange}
        cursorPaginationEnabled
        onCursorPaginationChange={onPaginationChange}
        pageSize={pageSize}
        cursorOptions={{
          next: nextToken,
          prev: prevToken[prevToken.length - 1] ?? "",
        }}
        isManual
        tools={[
          <div key="date-range-picker" className="z-20">
            <DateRangePicker
              placeholder="Transaction Date"
              onChange={onDateChange}
              value={dateRange}
              className="w-48 leading-6"
              isClearable
              onClear={onClearDate}
            />
          </div>,
        ]}
        filters={tableFilters}
      />
    </Page>
  );
};

export default Activities;
